import { FC } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Button } from "src/domains/shared/components/Button";
import { OverflowTooltip } from "src/domains/shared/components/OverflowTooltip";
import { SIGNUP_FIELD } from "src/domains/shared/constants/constants";
import { COLOR_CONSTANTS } from "src/domains/shared/constants/styles";
import useLocale from "src/domains/shared/hooks/localeHook/useLocale";
import { useTimezone } from "src/domains/shared/hooks/useTimezone";
import { ERROR_SURFACE, LineGlyph, SkeletonBone, SkeletonReveal } from "src/domains/shared/components/StateTransition";
import { MYPAGE_AVATAR_TONES, MYPAGE_CARD, MYPAGE_SECONDARY_ICON } from "./myPageStyles";
import { EditableAccountField, useAccountInfo } from "./hooks/useAccountInfo";
import { useAccountWithdrawal } from "./hooks/useAccountWithdrawal";
import { AccountInfoRow, AccountInfoRowInput, AccountInfoRowMode } from "./components/AccountInfoRow";
import { WithdrawDialog } from "./components/WithdrawDialog";

type AccountRowKey = EditableAccountField | "email" | "timezone";

interface AccountRowDefinition {
  key: AccountRowKey;
  labelKey: `myPage.account.${"name" | "email" | "phone" | "organization" | "role" | "field" | "timezone"}`;
  skeletonWidth: string;
  editable: boolean;
}

const ACCOUNT_ROWS: AccountRowDefinition[] = [
  { key: "name", labelKey: "myPage.account.name", skeletonWidth: "30%", editable: true },
  { key: "email", labelKey: "myPage.account.email", skeletonWidth: "55%", editable: false },
  { key: "phone_number", labelKey: "myPage.account.phone", skeletonWidth: "40%", editable: true },
  { key: "company", labelKey: "myPage.account.organization", skeletonWidth: "35%", editable: true },
  { key: "job", labelKey: "myPage.account.role", skeletonWidth: "30%", editable: true },
  { key: "field", labelKey: "myPage.account.field", skeletonWidth: "20%", editable: true },
  { key: "timezone", labelKey: "myPage.account.timezone", skeletonWidth: "45%", editable: false },
];

const PROFILE_CARD_HEIGHT_PX = 76;
const AVATAR_SIZE_PX = 44;

export const AccountInfo: FC = () => {
  const { t } = useTranslation(["projects", "common"]);
  const [locale] = useLocale();
  const { timezone } = useTimezone();
  const account = useAccountInfo();
  const { userInfo, renderState, editingField, draft, validation, isSaving } = account;
  const withdrawal = useAccountWithdrawal();

  const branch = (() => {
    if (renderState.state === "error") return "failed" as const;
    if (renderState.state === "pending" || !userInfo) return "loading" as const;
    return "ready" as const;
  })();

  const fieldOptions = SIGNUP_FIELD.map((option) => option[locale.locale as "ko" | "en"] ?? option.ko);

  const errorText = (() => {
    if (validation.ok) return null;
    if (validation.reason === "phone") return t("myPageModal.account.phoneError", { ns: "projects" });
    return t("myPageModal.account.requiredError", { ns: "projects" });
  })();

  const valueOf = (key: AccountRowKey) => {
    if (key === "timezone") return `${timezone} ${t("myPage.account.timezoneAutoDetected", { ns: "projects" })}`;
    return String(userInfo?.[key] ?? "");
  };

  const inputOf = (key: AccountRowKey): AccountInfoRowInput => {
    if (key === "field") return { kind: "select", options: fieldOptions };
    return { kind: "text", inputMode: key === "phone_number" ? "numeric" : "text" };
  };

  const rowModeOf = (key: AccountRowKey): AccountInfoRowMode => {
    if (branch === "loading") return "loading";
    if (branch === "failed") return "failed";
    return editingField === key ? "edit" : "view";
  };

  const subline = [userInfo?.company, userInfo?.job].filter(Boolean).join(" · ");

  return (
    <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px", display: "flex", flexDirection: "column", gap: "12px" }}>
      <Box sx={{ ...MYPAGE_CARD, height: PROFILE_CARD_HEIGHT_PX, flexShrink: 0, display: "flex", alignItems: "center", padding: "0 16px" }}>
        {branch === "loading" && (
          <SkeletonReveal label={t("myPage.menu.accountInfo", { ns: "projects" })}>
            <Box sx={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <SkeletonBone width={AVATAR_SIZE_PX} height={AVATAR_SIZE_PX} radius={8} />
              <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                <SkeletonBone width={72} height={12} />
                <SkeletonBone width={140} height={10} />
              </Box>
            </Box>
          </SkeletonReveal>
        )}
        {branch === "failed" && (
          <Box role="alert" sx={{ flex: 1, minWidth: 0, display: "flex", alignItems: "center", gap: "14px" }}>
            <Box sx={{ ...avatarSx, backgroundColor: ERROR_SURFACE, color: COLOR_CONSTANTS.error.main }}>
              <LineGlyph name="alert" size={20} />
            </Box>
            <Typography sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}>
              {t("queryState.loadFailedGeneric", { ns: "common" })}
            </Typography>
            <Button
              size="small"
              emphasis="outlined"
              intent="inherit"
              onClick={account.refetchUser}
              startIcon={<LineGlyph name="refresh" size={14} />}
            >
              {t("queryState.retry", { ns: "common" })}
            </Button>
          </Box>
        )}
        {branch === "ready" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: "14px", minWidth: 0 }}>
            <Box aria-hidden="true" sx={{ ...avatarSx, backgroundColor: MYPAGE_AVATAR_TONES[0], color: COLOR_CONSTANTS.common.white_states.main, fontSize: 16, fontWeight: 700 }}>
              {(userInfo?.name ?? "U").charAt(0)}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", minWidth: 0 }}>
              <OverflowTooltip text={userInfo?.name ?? ""} component="h3" placement="bottom-start" sx={{ fontSize: 14, fontWeight: 700, margin: 0 }} />
              <OverflowTooltip text={subline} placement="bottom-start" sx={{ fontSize: 12, color: COLOR_CONSTANTS.text.secondary }} />
            </Box>
          </Box>
        )}
      </Box>

      <Box sx={{ ...MYPAGE_CARD, flexShrink: 0 }}>
        {ACCOUNT_ROWS.map((row) => {
          const isEditableKey = row.key !== "email" && row.key !== "timezone";
          return (
            <AccountInfoRow
              key={row.key}
              label={t(row.labelKey, { ns: "projects" })}
              value={valueOf(row.key)}
              mode={rowModeOf(row.key)}
              editable={row.editable}
              editLocked={editingField !== null}
              skeletonWidth={row.skeletonWidth}
              input={inputOf(row.key)}
              draft={draft}
              errorText={editingField === row.key ? errorText : null}
              saving={isSaving}
              onEdit={() => isEditableKey && account.startEdit(row.key as EditableAccountField)}
              onDraftChange={account.changeDraft}
              onConfirm={account.confirmEdit}
              onCancel={account.cancelEdit}
            />
          );
        })}
      </Box>

      <Box sx={{ marginTop: "auto", display: "flex", justifyContent: "flex-end" }}>
        <Box
          component="button"
          type="button"
          onClick={withdrawal.openDialog}
          sx={{
            visibility: branch === "ready" ? "visible" : "hidden",
            border: 0,
            padding: "2px 0",
            background: "transparent",
            fontFamily: "inherit",
            fontSize: 11,
            color: COLOR_CONSTANTS.text.disabled,
            cursor: "pointer",
            transition: "color 120ms ease",
            "&:hover": { color: COLOR_CONSTANTS.text.secondary, textDecoration: "underline" },
            "&:focus-visible": { outline: "none", color: MYPAGE_SECONDARY_ICON, textDecoration: "underline" },
          }}
        >
          {t("myPage.menu.withdrawal", { ns: "projects" })}
        </Box>
      </Box>

      <WithdrawDialog
        open={withdrawal.open}
        processing={withdrawal.processing}
        errorText={withdrawal.errorText}
        onCancel={withdrawal.close}
        onConfirm={withdrawal.confirm}
      />
    </Box>
  );
};

const avatarSx = {
  width: AVATAR_SIZE_PX,
  height: AVATAR_SIZE_PX,
  borderRadius: "8px",
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
} as const;
